import { afterNextRender, Injectable, computed, effect, inject } from '@angular/core';
import { TokenService } from './token.service';
import { SessionStore } from '../stores/session.store';

/**
 * Facade over SessionStore — provides a clean public API to components.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {
  private sessionStore = inject(SessionStore);
  private tokenService = inject(TokenService);

  public isAuthenticated = computed(() => this.sessionStore.isAuthenticated());
  public isRefreshing = computed(() => this.sessionStore.isRefreshing());
  public showSessionWarning = computed(() => {
    if (!this.sessionStore.showSessionWarning()) return false;
    // Auto-dismiss the moment the inactivity logout time has passed
    // (handles tab-throttled logout timers not yet having fired)
    const logoutTime = this.sessionStore.inactivityLogoutTime();
    return !!logoutTime && logoutTime > Date.now();
  });
  public sessionExpiryTime = computed(() => this.sessionStore.sessionExpiryTime());
  public errorMessage = computed(() => this.sessionStore.errorMessage());
  public timeUntilExpiry = computed(() => this.sessionStore.timeUntilExpiry());
  public isSessionExpiringSoon = computed(() => this.sessionStore.isSessionExpiringSoon());
  public timeRemainingDisplay = computed(() => this.sessionStore.timeRemainingDisplay());
  public progressPercent = computed(() => this.sessionStore.progressPercent());

  constructor() {
    // Defer until after bootstrap so AuthStore can finish initializing first
    afterNextRender(() => {
      this.sessionStore.initializeSession();
    });

    effect(() => {
      this.tokenService.getAccessTokenValue();
      this.sessionStore.syncWithTokenService();
    });
  }

  public async refreshToken(): Promise<boolean> {
    return this.sessionStore.refreshToken();
  }

  public async handleUnauthorizedRequest(): Promise<boolean> {
    return this.sessionStore.handleUnauthorizedRequest();
  }

  public async extendSession(): Promise<void> {
    await this.sessionStore.extendSession();
  }

  public dismissSessionWarning(): void {
    this.sessionStore.dismissSessionWarning();
  }

  public logout(): void {
    this.sessionStore.logout();
  }
}